// Survey Configuration Registry
// Central registry for all climate survey configurations

import { SurveyConfig, GradeLevel, RespondentType } from './schema';

// Import all survey configurations
import studentK2 from './student-k2.json';
import student35 from './student-3-5.json';
import student68 from './student-6-8.json';
import studentHS from './student-high-school.json';
import virtualK8 from './student-virtual-k8.json';
import virtualHS from './student-virtual-hs.json';
// Staff survey temporarily disabled
// import staffClimate from './staff-climate.json';

// Type assertion for imported JSON
const surveys: SurveyConfig[] = [
  studentK2 as unknown as SurveyConfig,
  student35 as unknown as SurveyConfig,
  student68 as unknown as SurveyConfig,
  studentHS as unknown as SurveyConfig,
  virtualK8 as unknown as SurveyConfig,
  virtualHS as unknown as SurveyConfig,
  // staffClimate as unknown as SurveyConfig, // temporarily disabled
];

// Registry: surveyId -> SurveyConfig
export const surveyRegistry: Record<string, SurveyConfig> = surveys.reduce(
  (acc, survey) => {
    acc[survey.id] = survey;
    return acc;
  },
  {} as Record<string, SurveyConfig>
);

// Get a survey configuration by ID
export function getSurveyConfig(surveyId: string): SurveyConfig | null {
  return surveyRegistry[surveyId] || null;
}

// Get all survey IDs
export function getAllSurveyIds(): string[] {
  return Object.keys(surveyRegistry);
}

// Get surveys by grade level
export function getSurveysByGradeLevel(gradeLevel: GradeLevel): SurveyConfig[] {
  return surveys.filter((survey) => survey.gradeLevel === gradeLevel);
}

// Get survey for a specific grade level (primary survey)
export function getSurveyForGradeLevel(
  gradeLevel: GradeLevel
): SurveyConfig | null {
  const gradeSurveys = getSurveysByGradeLevel(gradeLevel);
  return gradeSurveys.length > 0 ? gradeSurveys[0] : null;
}

// Grade level to survey ID mapping
export const GRADE_LEVEL_TO_SURVEY: Record<GradeLevel, string> = {
  'k-2': 'student-k2',
  '3-5': 'student-3-5',
  '6-8': 'student-6-8',
  'high-school': 'student-high-school',
  'virtual-k8': 'student-virtual-k8',
  'virtual-hs': 'student-virtual-hs',
};

// Get all surveys (useful for admin views)
export function getAllSurveys(): SurveyConfig[] {
  return [...surveys];
}

// Get surveys by respondent type
export function getSurveysByRespondentType(type: RespondentType): SurveyConfig[] {
  return surveys.filter((survey) => survey.respondentType === type);
}

// Get the appropriate staff survey, optionally filtered by school identifier
export function getSurveyForStaff(schoolIdentifier?: string | null): SurveyConfig | null {
  const staffSurveys = getSurveysByRespondentType('staff');

  if (schoolIdentifier) {
    // Try to find a school-scoped survey first
    const scoped = staffSurveys.find(
      (s) => s.schoolScope && s.schoolScope.includes(schoolIdentifier)
    );
    if (scoped) return scoped;
  }

  // Fall back to a district-wide survey (no schoolScope set)
  const districtWide = staffSurveys.find((s) => !s.schoolScope);
  return districtWide || staffSurveys[0] || null;
}

// Re-export types and defaults for convenience
export * from './schema';
export * from './defaults';
